import type { FieldPolicy } from '@apollo/client/cache';
import type { PageInfo } from '../../generated';
import type { FieldPolicyWithVars } from '../types/TypePolicies';

type KeyArgs = FieldPolicy<any>['keyArgs'];

type ObjectWithId = {
  id: string;
};

/*
 This method is based off Apollo's relay-style field policy:
 https://github.com/apollographql/apollo-client/blob/main/src/utilities/policies/pagination.ts
*/
export function cursorPaginationFieldPolicy(
  keyArgs: KeyArgs = false,
  filterOutNullResults = false,
  getNodeId: (node: any) => string | undefined = (node) => node?.id
): Pick<FieldPolicyWithVars, 'keyArgs' | 'merge'> {
  return {
    keyArgs,

    merge(
      existing = makeEmptyData(),
      incoming,
      { args, isReference, readField }
    ) {
      const getId = (node: any) =>
        node && isReference(node)
          ? readField<string>('id', node) ?? getNodeId(node)
          : getNodeId(node);

      const _nodes = filterOutNullResults
        ? incoming?.nodes?.filter((node: any) => node)
        : incoming?.nodes;

      const incomingNodes =
        _nodes?.map((node: any) => ({
          id: getId(node) || getId(node?.result),
          ...node,
        })) || [];

      let nodes: any[];
      let pageInfo: Partial<PageInfo>;

      if (args?.page?.before) {
        // Backwards pagination: prepend incoming before existing nodes
        let suffix = existing.nodes;
        const index = suffix.findIndex(
          (node: any) => getId(node) === args.page.before
        );
        if (index >= 0) {
          suffix = suffix.slice(index);
        }
        const suffixIds = suffix.map((n: ObjectWithId) => getId(n));
        nodes = [...excludeExisting(suffixIds, incomingNodes), ...suffix];
        pageInfo = {
          ...existing.pageInfo,
          ...incoming?.pageInfo,
          // Preserve forward-pagination state since the end hasn't changed
          endCursor: existing.pageInfo?.endCursor,
          hasNextPage: existing.pageInfo?.hasNextPage,
        };
      } else {
        // Forward pagination: append incoming after existing nodes
        let prefix = existing.nodes;
        if (args?.page?.after) {
          // handle if incoming nodes are in the middle of cached data
          const index = prefix.findIndex(
            (node: any) => getId(node) === args.page.after
          );
          if (index >= 0) {
            prefix = prefix.slice(0, index + 1);
          }
        } else if (incoming?.nodes) {
          prefix = [];
        }
        const existingIds = prefix.map((n: ObjectWithId) => getId(n));
        nodes = [...prefix, ...excludeExisting(existingIds, incomingNodes)];
        pageInfo = { ...incoming?.pageInfo };
      }

      return {
        ...existing,
        ...incoming,
        nodes,
        pageInfo,
      };
    },
  };
}

export const makeEmptyData = (hasNextPage = true) => ({
  nodes: [],
  pageInfo: {
    endCursor: null,
    hasNextPage,
    hasPreviousPage: false,
  },
});

export const excludeExisting = (
  ids: string[],
  incomingNodes: ObjectWithId[]
): ObjectWithId[] => {
  return incomingNodes.filter((n) => !ids.includes(n.id));
};
